import React, { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";

/**
 * Convert HSL string to hex color
 */
const hslToHex = (hslString) => {
  if (!hslString) return "#000000";
  
  // Parse HSL string "212 85% 35%" to values
  const parts = hslString.split(/\s+/);
  if (parts.length !== 3) return "#000000";
  
  const h = parseInt(parts[0]);
  const s = parseInt(parts[1]) / 100;
  const l = parseInt(parts[2]) / 100;
  
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  
  let r = 0, g = 0, b = 0;
  
  if (h >= 0 && h < 60) {
    r = c; g = x; b = 0;
  } else if (h >= 60 && h < 120) {
    r = x; g = c; b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0; g = c; b = x;
  } else if (h >= 180 && h < 240) {
    r = 0; g = x; b = c;
  } else if (h >= 240 && h < 300) {
    r = x; g = 0; b = c;
  } else {
    r = c; g = 0; b = x;
  }
  
  const toHex = (n) => {
    const hex = Math.round((n + m) * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Convert hex color to HSL string
 */
const hexToHsl = (hex) => {
  // Remove # if present
  hex = hex.replace(/^#/, '');
  
  // Parse hex values
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  
  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);
  
  return `${h} ${s}% ${l}%`;
};

export const ColorPicker = ({ label, value, onChange, description }) => {
  const hexValue = hslToHex(value);
  
  const handleColorChange = (e) => {
    const hex = e.target.value;
    const hsl = hexToHsl(hex);
    onChange(hsl);
  };
  
  const handleHslChange = (e) => {
    onChange(e.target.value);
  };
  
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}
      <div className="flex items-center gap-4">
        {/* Color preview */}
        <div
          className="w-16 h-16 rounded-lg border-2 border-border shadow-sm"
          style={{ backgroundColor: hexValue }}
        />
        
        {/* Color picker input */}
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <Input
              type="color"
              value={hexValue}
              onChange={handleColorChange}
              className="w-20 h-10 cursor-pointer"
            />
            <Input
              type="text"
              value={hexValue}
              onChange={(e) => handleColorChange({ target: { value: e.target.value }})}
              placeholder="#000000"
              className="flex-1"
            />
          </div>
          
          {/* HSL input */}
          <Input
            type="text"
            value={value}
            onChange={handleHslChange}
            placeholder="212 85% 35%"
            className="text-sm"
          />
          <p className="text-xs text-muted-foreground">
            Format HSL: hue saturation% lightness%
          </p>
        </div>
      </div>
    </div>
  );
};

export default ColorPicker;
